import axiosInstance from './axiosInstance';

export interface User {
  id: number;
  name: string;
  email?: string;
  closed: boolean;
  avatarUrl?: string; // URL аватарки пользователя
  bio?: string; // Биография/подпись пользователя
}

export interface Media {
  id: number;
  mediaType: string; // 'avatar', 'site_photo', 'chat_file', etc.
  entityType: string; // 'user', 'site', 'message', etc.
  entityId: number;
  fileId: string;
  sizes: MediaSize[];
  width?: number;
  height?: number;
  fileSize: number;
  mimeType: string;
  isActive: boolean;
  metadata?: Record<string, any>;
  createdAt: string;
  createdBy?: number;
}

export interface MediaSize {
  sizeType: 'thumbnail' | 'small' | 'medium' | 'large' | 'original' | 'avatar' | 'full';
  url: string;
  width: number;
  height: number;
  fileSize: number;
}

export interface UpdateUserRequest {
  name?: string;
  closed?: boolean;
  bio?: string; // Биография/подпись пользователя
}

class UsersApi {
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await axiosInstance.get<User[]>('/Users');
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getActiveUsers(): Promise<User[]> {
    try {
      const response = await axiosInstance.get<User[]>('/Users');
      return response.data.filter(user => !user.closed);
    } catch (error) {
      console.error('Error fetching active users:', error);
      throw error;
    }
  }

  async getUser(id: number): Promise<User> {
    try {
      const response = await axiosInstance.get<User>(`/Users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async updateUser(id: number, name: string, closed: boolean): Promise<boolean> {
    try {
      const response = await axiosInstance.put(`/Users/${id}`, {
        name,
        closed
      });
      return response.status === 200;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async updateUserDetails(id: number, userData: UpdateUserRequest): Promise<void> {
    try {
      await axiosInstance.put(`/Users/${id}`, userData);
    } catch (error) {
      console.error('Error updating user details:', error);
      throw error;
    }
  }

  async uploadAvatar(id: number, file: File): Promise<Media> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post<{ success: boolean; data: Media }>(
        `/Users/${id}/avatar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  }

  async getAvatar(userId: number): Promise<Media | null> {
    try {
      // API теперь возвращает 200 OK с null, если аватарка не найдена
      // Это нормальная ситуация, не ошибка
      const response = await axiosInstance.get<Media | null>(`/Users/${userId}/avatar`);
      return response.data; // Может быть null, если аватарка не найдена
    } catch (error: any) {
      // Обрабатываем ошибки на случай, если сервер все еще возвращает 404 (backward compatibility)
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error getting avatar:', error);
      throw error;
    }
  }

  async getUserAvatars(userId: number): Promise<Media[]> {
    try {
      const response = await axiosInstance.get<Media[]>(`/Users/${userId}/avatars`);
      return response.data;
    } catch (error) {
      console.error('Error getting user avatars:', error);
      throw error;
    }
  }

  async deleteAvatar(userId: number, mediaId: number): Promise<void> {
    try {
      await axiosInstance.delete(`/Users/${userId}/avatar/${mediaId}`);
    } catch (error) {
      console.error('Error deleting avatar:', error);
      throw error;
    }
  }

  async activateAvatar(userId: number, mediaId: number): Promise<void> {
    try {
      await axiosInstance.post(`/Users/${userId}/avatar/${mediaId}/activate`);
    } catch (error) {
      console.error('Error activating avatar:', error);
      throw error;
    }
  }

  async uploadCover(id: number, file: File): Promise<Media> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post<{ success: boolean; data: Media }>(
        `/Users/${id}/cover`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error uploading cover:', error);
      throw error;
    }
  }

  async getCover(userId: number): Promise<Media | null> {
    try {
      // API теперь возвращает 200 OK с null, если cover не найдена
      // Это нормальная ситуация, не ошибка
      const response = await axiosInstance.get<Media | null>(`/Users/${userId}/cover`);
      return response.data; // Может быть null, если cover не найдена
    } catch (error: any) {
      // Обрабатываем ошибки на случай, если сервер все еще возвращает 404 (backward compatibility)
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error getting cover:', error);
      throw error;
    }
  }

  async deleteCover(userId: number, mediaId: number): Promise<void> {
    try {
      await axiosInstance.delete(`/Users/${userId}/cover/${mediaId}`);
    } catch (error) {
      console.error('Error deleting cover:', error);
      throw error;
    }
  }
}

export const usersApi = new UsersApi();
